import React, { useCallback } from 'react';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { useSelector } from 'react-redux';
import {
  defaultProUnicornPlanSelector,
  getCurrentSubscription,
} from '@mm/state/selectors/subscriptionSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  selectCurrentUser,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { landmineNames } from '@mm/constants/landmine-names';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useLandmineOpensCRUDActions } from '@mm/state/actions/LandmineOpensCRUDActions';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';

export const useAmbassadorPaywall = () => {
  const usersCRUDActions = useUsersCRUDActions();

  const proUnicornPlan = useSelector(defaultProUnicornPlanSelector);

  const facebookPage = useSelector(activeFacebookPageSelector);
  const companyId = useSelector(selectActiveCompanyId);

  const landmineOpensCRUDActions = useLandmineOpensCRUDActions();

  const user = useSelector(selectCurrentUser);
  const session = useSelector(selectSessionSelector);
  const { userAttributes } = session;
  const modalActions = useModalActions();

  const appNavigationHistory = useSelector(
    (state: StoreState) => state.view.appNavigationHistory,
  );

  const subscriptionFromSubscriptions = useSelector(
    getCurrentSubscription(facebookPage?.id),
  );

  const currentSubscription: SubscriptionModel | undefined =
    facebookPage?.subscription || subscriptionFromSubscriptions;

  const showPaywall = userAttributes.showPaywall && !currentSubscription;

  const openPaywall = useCallback(
    async (e?: React.MouseEvent<HTMLElement>) => {
      if (showPaywall && proUnicornPlan) {
        const res = await landmineOpensCRUDActions.create({
          landmineName: landmineNames['AMBASSADOR_REFERRAL_PAYWALL'],
          navigationHistory: appNavigationHistory,
          planName: proUnicornPlan.name,
          billingOpened: true,
        });

        e?.stopPropagation?.();
        e?.preventDefault?.();
        modalActions.openBillingModal({
          facebookPage,
          landmineOpenId: res?.id,
          plan: proUnicornPlan,
          paywall: true,
          onSuccessBilling: () => {
            if (user?.id) {
              usersCRUDActions.updateUserAttributes(companyId, user.id, {
                showPaywall: false,
              });
            }
          },
        });
      }
    },
    [facebookPage, proUnicornPlan, showPaywall],
  );

  return {
    showPaywall,
    openPaywall,
  };
};
